import { useMemo } from 'react'
import { useApiQuery } from '../../../hooks/useApiQuery.js'
import { useMarketplaceParams } from './useMarketplaceParams.js'
import { useMarketplaceQueryParams } from './useMarketplaceQueryParams.js'
import { LIST_TYPES, POLLING_INTERVAL } from '../utils/marketplaceConstants.js'

/**
 * Single source of truth for marketplace category counts
 * (listOrdersCounts / listTripsCounts).
 *
 * Counts are category-independent (the endpoint returns counts for every
 * category in one call), so the query key is derived only from search,
 * markets, and the URL-derived admin filters — NOT the current category.
 *
 * Every consumer (sidebar badges, overview dropdown, search-redirect) reads
 * through this one definition so the query key can never drift. React Query
 * dedupes the identical key across subscribers into a single request + poll.
 */

const COUNTS_STALE_TIME = 30000

/**
 * Build the counts query method + payload + key for a given param set.
 * Exported so imperative callers (e.g. the search-redirect) can reuse the
 * exact same key and feed/read the shared cache.
 *
 * @param {Object} args
 * @param {string} args.listType - LIST_TYPES.ORDERS | LIST_TYPES.TRIPS
 * @param {string} args.search - Search query
 * @param {string[]} args.markets - Selected market UIDs
 * @param {Object} args.adminFilterQueryProps - URL-derived filter query props
 * @returns {{ method: string, params: Object, key: Array }}
 */
export const getMarketplaceCountsQuery = ({
  listType,
  search,
  markets,
  adminFilterQueryProps,
}) => {
  const method =
    listType === LIST_TYPES.TRIPS
      ? 'getMarketplaceTripsCounts'
      : 'getMarketplaceOrdersCounts'

  const params = {
    search: search || undefined,
    marketUids: markets.length > 0 ? markets : undefined,
    ...adminFilterQueryProps,
  }

  return { method, params, key: [method, params] }
}

/**
 * useMarketplaceCounts
 *
 * Fetches marketplace category counts for the current URL state. Always
 * enabled while mounted; polls in the background. Returns the counts object
 * keyed by category ({ open, pending, tendered, in_progress, finished }).
 */
export const useMarketplaceCounts = () => {
  const { search, markets, listType } = useMarketplaceParams()
  const { adminFilterQueryProps, apiParamsReady } = useMarketplaceQueryParams()

  const { params, key } = useMemo(
    () =>
      getMarketplaceCountsQuery({
        listType,
        search,
        markets,
        adminFilterQueryProps,
      }),
    [listType, search, markets, adminFilterQueryProps],
  )

  const {
    data: counts,
    isLoading: isQueryLoading,
    isFetching,
    refetch,
  } = useApiQuery({
    key,
    payload: params,
    enabled: apiParamsReady,
    staleTime: COUNTS_STALE_TIME,
    poll: POLLING_INTERVAL,
    showToast: false,
  })

  return {
    counts: counts || {},
    isLoading: isQueryLoading || !apiParamsReady,
    isFetching,
    refetch,
  }
}

export { COUNTS_STALE_TIME }
